/**
 * FileDropzone Component
 *
 * Drag-and-drop file selection component with validation.
 * Supports multiple file uploads with file type and size validation.
 *
 * @module components/FileDropzone
 */

import React, { useCallback, useState } from 'react';
import { Upload, FileText, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import type {
  FileDropzoneProps,
  LocalFile,
} from '@/features/upload/types/upload.types';
import {
  DEFAULT_ACCEPT_FORMATS,
  DEFAULT_MAX_FILE_SIZE,
  DEFAULT_MAX_FILES,
  validateFile,
  formatFileSize,
} from '@/features/upload/types/upload.types';

/**
 * FileDropzone Component
 *
 * Provides drag-and-drop file selection with validation.
 *
 * @example
 * ```tsx
 * <FileDropzone
 *   onFilesSelected={(files) => setFiles(files)}
 *   accept=".parquet,.csv,.json"
 *   maxSize={100 * 1024 * 1024}
 *   maxFiles={100}
 * />
 * ```
 */
export const FileDropzone: React.FC<FileDropzoneProps> = ({
  onFilesSelected,
  accept = DEFAULT_ACCEPT_FORMATS,
  maxSize = DEFAULT_MAX_FILE_SIZE,
  maxFiles = DEFAULT_MAX_FILES,
  multiple = true,
  disabled = false,
  className,
  children,
}) => {
  const [isDragActive, setIsDragActive] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  /**
   * Process and validate selected files
   */
  const processFiles = useCallback(
    (fileList: FileList | null) => {
      if (!fileList || fileList.length === 0) return;

      const files: LocalFile[] = [];
      const newErrors: string[] = [];

      // Check max files limit
      if (fileList.length > maxFiles) {
        newErrors.push(`Too many files. Maximum ${maxFiles} files allowed.`);
        setErrors(newErrors);
        return;
      }

      // Validate each file
      for (let i = 0; i < fileList.length; i++) {
        const file = fileList[i];

        // Validate file
        const validation = validateFile(file, { accept, maxSize });

        if (validation.valid) {
          // Create LocalFile with unique ID
          const localFile: LocalFile = Object.assign(file, {
            id: `${Date.now()}-${i}-${file.name}`,
            status: 'pending' as const,
            uploadProgress: 0,
            format: validation.metadata?.format,
          });

          files.push(localFile);
        } else {
          newErrors.push(...validation.errors.map(err => `${file.name}: ${err}`));
        }
      }

      // Update errors
      setErrors(newErrors);

      // Call callback with valid files
      if (files.length > 0) {
        onFilesSelected(files);
      }
    },
    [onFilesSelected, accept, maxSize, maxFiles]
  );

  /**
   * Handle file input change
   */
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      processFiles(e.target.files);
      // Reset input value to allow selecting same files again
      e.target.value = '';
    },
    [processFiles]
  );

  /**
   * Handle drag enter
   */
  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  }, []);

  /**
   * Handle drag over
   */
  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  /**
   * Handle drag leave
   */
  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  }, []);

  /**
   * Handle drop
   */
  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragActive(false);

      if (disabled) return;

      const files = e.dataTransfer.files;
      processFiles(files);
    },
    [disabled, processFiles]
  );

  /**
   * Handle click to open file dialog
   */
  const handleClick = useCallback(() => {
    if (disabled) return;
    document.getElementById('file-input')?.click();
  }, [disabled]);

  return (
    <div className={cn('space-y-4', className)}>
      {/* Dropzone */}
      <div
        className={cn(
          'relative border-2 border-dashed rounded-lg transition-colors duration-200',
          'flex flex-col items-center justify-center p-8 cursor-pointer',
          'hover:border-primary hover:bg-accent/50',
          isDragActive && 'border-primary bg-accent',
          disabled && 'opacity-50 cursor-not-allowed hover:border-border hover:bg-background'
        )}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        {/* Hidden file input */}
        <input
          id="file-input"
          type="file"
          multiple={multiple}
          accept={accept}
          onChange={handleChange}
          disabled={disabled}
          className="hidden"
          aria-label="File input"
        />

        {/* Custom content or default UI */}
        {children || (
          <>
            {/* Icon */}
            <div className="mb-4">
              {isDragActive ? (
                <FileText className="h-12 w-12 text-primary animate-bounce" />
              ) : (
                <Upload className="h-12 w-12 text-muted-foreground" />
              )}
            </div>

            {/* Text */}
            <div className="text-center space-y-2">
              <p className="text-sm font-medium">
                {isDragActive
                  ? 'Drop files here'
                  : 'Drag & drop files here, or click to select'}
              </p>
              <p className="text-xs text-muted-foreground">
                Supported formats: {accept.split(',').join(', ')}
              </p>
              <p className="text-xs text-muted-foreground">
                Max file size: {formatFileSize(maxSize)} | Max files: {maxFiles}
              </p>
            </div>
          </>
        )}
      </div>

      {/* Errors */}
      {errors.length > 0 && (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
            <div className="flex-1 space-y-1">
              <p className="text-sm font-medium text-destructive">
                {errors.length} validation error{errors.length !== 1 ? 's' : ''}
              </p>
              <ul className="list-disc list-inside space-y-1">
                {errors.map((error, index) => (
                  <li key={index} className="text-xs text-destructive/90">
                    {error}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Help text */}
      <p className="text-xs text-muted-foreground text-center">
        Files will be validated before upload. Invalid files will be rejected.
      </p>
    </div>
  );
};
